<template>
    <div class="w-full">
        <NuxtLink
            :to="localePath('/contact-support')"
            class="mb-6 inline-flex items-center gap-2 text-primary hover:underline"
        >
            <ArrowLeftIcon class="h-4 w-4" aria-hidden="true"/>
            {{ t('back') }}
        </NuxtLink>

        <PageTitle>{{ t('title') }}</PageTitle>

        <div class="grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(18rem,0.65fr)]">
            <YGroupContainer as="section" class="flex flex-col gap-6 sm:p-8" aria-labelledby="problem-heading">
                <div>
                    <h1 id="problem-heading" class="max-w-2xl font-fancy text-3xl font-medium text-primary sm:text-4xl">
                        {{ t('heading') }}
                    </h1>
                    <p class="mt-4 max-w-2xl leading-6 text-text-light">{{ t('introduction') }}</p>
                </div>

                <div class="rounded-lg border border-background-accent-dark bg-background p-5 sm:p-6">
                    <div class="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h2 class="font-fancy text-2xl font-bold text-text">{{ t('email.title') }}</h2>
                            <p class="mt-2 text-sm leading-6 text-text-light">{{ t('email.description') }}</p>
                            <p class="mt-2 text-sm leading-6 text-text-light">{{ t('email.knownContact') }}</p>
                        </div>
                        <div class="flex shrink-0 flex-col gap-3 sm:items-stretch">
                            <a
                                :href="emailHref"
                                class="inline-flex items-center justify-center gap-2 rounded border border-primary bg-primary px-4 py-2 font-medium text-background transition-colors hover:bg-primary-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                            >
                                <svg
                                    class="h-4 w-4"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="1.8"
                                    aria-hidden="true"
                                >
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75 12 12.75l8.25-6M5.25 19.5h13.5A2.25 2.25 0 0 0 21 17.25V6.75a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 6.75v10.5a2.25 2.25 0 0 0 2.25 2.25Z"/>
                                </svg>
                                {{ t('email.action') }}
                            </a>
                            <a
                                v-if="showWhatsApp"
                                :href="whatsappHref"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="whatsapp-button inline-flex items-center justify-center gap-2 rounded border px-4 py-2 font-medium transition-colors"
                            >
                                <font-awesome-icon :icon="faWhatsapp" class="h-5 w-5" aria-hidden="true"/>
                                {{ t('whatsapp.action') }}
                            </a>
                            <p
                                v-if="showWhatsApp && whatsappDisplayNumber"
                                class="max-w-56 text-center text-xs leading-5 text-text-light"
                            >
                                {{ t('whatsapp.alternative') }}
                                <strong class="block font-monos text-sm text-text">{{ whatsappDisplayNumber }}</strong>
                            </p>
                        </div>
                    </div>
                </div>
            </YGroupContainer>

            <YGroupContainer as="aside" class="p-6! sm:p-8!">
                <h2 class="font-fancy text-2xl font-bold text-text">{{ t('details.title') }}</h2>
                <p class="mt-2 text-sm leading-6 text-text-light">{{ t('details.description') }}</p>
                <ul class="mt-6 flex flex-col gap-5">
                    <li v-for="(detailKey, index) in supportDetailKeys" :key="detailKey" class="flex gap-4">
                        <span
                            class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-background"
                            aria-hidden="true"
                        >{{ index + 1 }}</span>
                        <div>
                            <h3 class="font-bold text-text">{{ t(`details.items.${ detailKey }.title`) }}</h3>
                            <p class="mt-1 text-sm leading-5 text-text-light">
                                {{ t(`details.items.${ detailKey }.description`) }}
                            </p>
                        </div>
                    </li>
                </ul>

                <div class="mt-8 border-t border-background-accent-dark pt-5">
                    <p class="text-xs font-bold uppercase tracking-wider text-text-light">{{ t('version') }}</p>
                    <p class="mt-1 font-monos text-sm text-primary">{{ cmsVersion }}</p>
                </div>
            </YGroupContainer>
        </div>
    </div>
</template>

<script setup lang="ts">
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'

const { t } = useI18n({ useScope: 'local' })
const appConfig = useAppConfig()
const runtimeConfig = useRuntimeConfig()
const localePath = useLocalePath()
const requestUrl = useRequestURL()
const userStore = useUserStore()

const supportDetailKeys = ['what', 'where', 'show'] as const
const showWhatsApp = computed(() => userStore.permissions.includes(UserPermission.user))
const whatsappDisplayNumber = ref('')
const cmsUrl = computed(() => runtimeConfig.public.publicHost || requestUrl.origin)
const whatsappHref = computed(() => new URL('/api/support/whatsapp', cmsUrl.value).toString())
const emailHref = computed(() => {
    const subject = encodeURIComponent(`CMS Error for ${ cmsUrl.value }`)
    return `mailto:info@yipp.nl?subject=${ subject }`
})
const cmsVersion = computed(() => {
    const version = runtimeConfig.public.version
    if (!version.tag) return `Version ${ version.commit_hash }`
    return `v${ version.tag } (${ version.commit_hash })`
})

watch(showWhatsApp, async visible => {
    if (!visible || whatsappDisplayNumber.value || import.meta.server) return
    try {
        const response = await $fetch<ApiResponse<{ number: string }>>('/api/support/whatsapp/contact')
        if (response.success) whatsappDisplayNumber.value = response.data.number
    } catch (error) {
        console.error('Could not load the WhatsApp support number', error)
    }
}, { immediate: true })

useHead(() => ({ title: `${ appConfig.headTitle } - ${ t('title') }` }))
</script>

<style scoped>
.whatsapp-button {
    color: #102a1b;
    background-color: #25d366;
    border-color: #128c4a;
}

.whatsapp-button:hover {
    background-color: #20bd5a;
}

.whatsapp-button:focus-visible {
    outline: 2px solid #128c4a;
    outline-offset: 2px;
}
</style>

<i18n lang="yaml">
en:
    back: Back to Contact & Support
    title: Report a CMS problem
    heading: Is something not working as expected?
    introduction: Sorry you are running into a problem. Please send us a little more information and we will be happy to take a look.
    email:
        title: Tell us about the problem
        description: Your email application will open with the CMS URL in the subject. If you can, please include the details from the checklist in your message.
        knownContact: If you already know someone at Yipp, you are also welcome to email that person directly.
        action: "Email info{'@'}yipp.nl"
    whatsapp:
        action: Chat on WhatsApp
        alternative: Prefer using WhatsApp on your phone? Add our support number directly.
    details:
        title: Could you tell us a little more?
        description: These details help us understand what happened and find a solution more quickly.
        items:
            what:
                title: What were you trying to do?
                description: Please tell us what you expected and what happened instead.
            where:
                title: Where did you notice it?
                description: If possible, include the page, content item, or URL where the problem occurred.
            show:
                title: Can you help us reproduce it?
                description: A screenshot and the steps you took would be very helpful, if available.
    version: Your CMS version
nl:
    back: Terug naar Contact & Ondersteuning
    title: Meld een CMS-probleem
    heading: Werkt er iets niet zoals verwacht?
    introduction: Vervelend dat je tegen een probleem aanloopt. Stuur ons alsjeblieft wat meer informatie, dan kijken we graag met je mee.
    email:
        title: Vertel ons over het probleem
        description: Je e-mailprogramma opent met de CMS-URL in het onderwerp. Voeg als het lukt de informatie uit de checklist toe aan je bericht.
        knownContact: Heb je al contact met iemand bij Yipp? Dan mag je die persoon natuurlijk ook rechtstreeks mailen.
        action: "Mail info{'@'}yipp.nl"
    whatsapp:
        action: Chat via WhatsApp
        alternative: Gebruik je WhatsApp liever op je telefoon? Voeg ons supportnummer direct toe.
    details:
        title: Kun je ons iets meer vertellen?
        description: Met deze informatie begrijpen we beter wat er is gebeurd en kunnen we sneller een oplossing vinden.
        items:
            what:
                title: Wat probeerde je te doen?
                description: Vertel ons alsjeblieft wat je verwachtte en wat er in plaats daarvan gebeurde.
            where:
                title: Waar zag je het probleem?
                description: Vermeld indien mogelijk de pagina, het contentitem of de URL waar het probleem optrad.
            show:
                title: Kun je ons helpen het probleem te herhalen?
                description: Een schermafbeelding en de stappen die je hebt genomen zouden ons erg helpen, als je die hebt.
    version: Jouw CMS-versie
</i18n>
